/**
 * Risk Classification Nomenclature Service (structural risk — ORDIN 1761).
 * Institution-level config: score ranges → criticality levels (label, color, default frequency).
 * One document per organization. Do not mix with procedural RPN or location risk profile.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DEFAULT_RISK_CLASSIFICATION_LEVELS,
  validateScoreRanges,
  getLevelForScore
} from '../utils/riskClassificationConstants';

const COLLECTION = 'risk_classification_nomenclature';

/**
 * Get nomenclature for an organization. Returns null if not configured.
 * @param {string} organizationId
 * @returns {Promise<{ id, organizationId, levels, updatedAt, updatedBy } | null>}
 */
export async function getNomenclature(organizationId) {
  if (!organizationId) return null;
  try {
    const ref = doc(db, COLLECTION, organizationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      organizationId: data.organizationId ?? snap.id,
      levels: data.levels ?? [],
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy ?? null
    };
  } catch (err) {
    console.error('[riskClassificationNomenclatureService] getNomenclature:', err);
    throw err;
  }
}

/**
 * Save nomenclature. Validates score ranges (gap-free, 2–7) before saving.
 * @param {string} organizationId
 * @param {Array<{ id, label, color, scoreMin, scoreMax, defaultFrequencyCode, displayOrder }>} levels
 * @param {string} userId
 * @returns {Promise<void>}
 */
export async function saveNomenclature(organizationId, levels, userId) {
  if (!organizationId || !userId) throw new Error('organizationId and userId are required');
  const validation = validateScoreRanges(levels);
  if (!validation.valid) throw new Error(validation.error);
  try {
    const ref = doc(db, COLLECTION, organizationId);
    await setDoc(ref, {
      organizationId,
      levels: levels.map(l => ({
        id: l.id,
        label: l.label,
        color: l.color,
        scoreMin: Number(l.scoreMin),
        scoreMax: Number(l.scoreMax),
        defaultFrequencyCode: l.defaultFrequencyCode,
        displayOrder: Number(l.displayOrder) ?? 0
      })),
      updatedAt: serverTimestamp(),
      updatedBy: userId
    }, { merge: true });
  } catch (err) {
    console.error('[riskClassificationNomenclatureService] saveNomenclature:', err);
    throw err;
  }
}

/**
 * Seed default nomenclature (ORDIN 1761 aligned) if none exists.
 * @param {string} organizationId
 * @param {string} userId
 * @returns {Promise<boolean>} true if seeded, false if already existed
 */
export async function seedDefaultNomenclature(organizationId, userId) {
  if (!organizationId || !userId) throw new Error('organizationId and userId are required');
  const existing = await getNomenclature(organizationId);
  if (existing && existing.levels && existing.levels.length > 0) return false;
  await saveNomenclature(organizationId, DEFAULT_RISK_CLASSIFICATION_LEVELS, userId);
  return true;
}

/**
 * Get criticality level for a total score (PC+FA+S) using org nomenclature.
 * @param {number} totalScore - 2–7
 * @param {Array} levels - from getNomenclature().levels
 * @returns {object|null} level { id, label, color, defaultFrequencyCode, ... } or null
 */
export function getCriticalityLevelForScore(totalScore, levels) {
  return getLevelForScore(totalScore, levels);
}

export const riskClassificationNomenclatureService = {
  getNomenclature,
  saveNomenclature,
  seedDefaultNomenclature,
  getCriticalityLevelForScore
};
